import React, { useState, Children, cloneElement } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Users, CheckCircle2, Anchor, Waves, Sun, Fish, Camera, Compass, MessageCircle, ArrowRight, Instagram, Mail, Star } from 'lucide-react';
import { Button } from '../components/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/Accordion';
// --- CONFIGURATION ---
// Users can easily edit these values
const CONFIG = {
  whatsappNumber: '5491122836110',
  instagramUrl: 'https://instagram.com/buceoynavegacion',
  email: 'info@buceoynavegacion.com',
  googleReviewsUrl: 'https://www.google.com/maps/place/Buceo+y+Navegaci%C3%B3n/@-34.5438357,-58.5536957,17z/data=!4m8!3m7!1s0x95bcb104909acf6f:0xf2823f46ec57e814!8m2!3d-34.5438357!4d-58.5511208!9m1!1b1!16s%2Fg%2F11xgpy4x_2?hl=en&entry=ttu&g_ep=EgoyMDI2MDYwMS4wIKXMDSoASAFQAw%3D%3D',
  paymentUrl: '#',
  // Checkouts específicos por tipo de participante (conectar luego)
  checkoutDiverUrl: 'https://checkout.librofy.online/cart/53353351479659:1',
  checkoutCompanionUrl: 'https://checkout.librofy.online/cart/53353482584427:1',
  totalSpots: 12,
  spotsLeft: 8,
  instructorPadiNumber: '397148',
  dates: 'Del 30 de agosto al 6 de septiembre, 2026',
  priceDiver: 1490,
  priceNonDiver: 1290,
  depositAmount: 100
};
// --- IMAGES ---
// Replace these URLs with real photos later
const IMAGES = {
  heroPoster: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=2070&auto=format&fit=crop',
  whyBonaire: ['https://images.unsplash.com/photo-1530053969600-caed2596d242?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1437622368342-7a3d73a34c8f?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=2070&auto=format&fit=crop' // Diver
  ],
  accommodation: {
    exterior: 'https://cdn.magicpatterns.com/uploads/uH3y6Vi1hJfNtdqaYnbg9j/Grand2BWindsock2BBonaire-2Bed-DeluxeVilla-135-1920w.webp',
    terraza: 'https://cdn.magicpatterns.com/uploads/4sXUa7EPzHXoZfxk4AsvPU/Grand2BWindsock2BBonaire-2Bed-DeluxeVilla-146-1920w.webp',
    living: 'https://cdn.magicpatterns.com/uploads/jjVbsrs76hPAEGsdUySWot/Grand2BWindsock2BBonaire-2Bed-DeluxeVilla-1001-1920w.webp',
    habitacion: 'https://cdn.magicpatterns.com/uploads/81arspwLQ5AkxM87bypAPo/Grand2BWindsock2BBonaire-2Bed-DeluxeVilla-IMG_1026-1920w.webp',
    bano: 'https://cdn.magicpatterns.com/uploads/tmVkrYQDQxasqtNmGedipz/Master2BBathroom-8a7e53aa-1920w.webp'
  },
  finalCta: 'https://images.unsplash.com/photo-1559494007-9f5847c49d94?q=80&w=1974&auto=format&fit=crop',
  // Logo oficial con fondo transparente — se muestra exactamente como fue subido
  logo: 'https://cdn.magicpatterns.com/uploads/41utpxk4CzbQZEoYHMxqpe/logo_sin_fondo.png'
};
// --- ANIMATION VARIANTS ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function Home() {
  const whatsappLink = `https://wa.me/${CONFIG.whatsappNumber}?text=Hola!%20Quiero%20información%20sobre%20el%20viaje%20a%20Bonaire%202026.`;
  const [packageType, setPackageType] = useState<'diver' | 'companion'>('diver');
  const scrollToParticipante = () => {
    document.getElementById('elegi-participante')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-teal-500 selection:text-white" data-id="element-2">
      {/* FLOATING BUTTONS */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3" data-id="element-3">
        <a href={whatsappLink} target="_blank" rel="noreferrer" className="group flex items-center justify-center w-14 h-14 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 transition-all hover:scale-105" data-id="element-4">
          <MessageCircle className="w-6 h-6" data-id="element-5" />
        </a>
        <Button size="lg" className="shadow-lg bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6 font-semibold hidden md:flex" onClick={scrollToParticipante} data-id="element-6">
          🤿 Quiero sumarme al grupo
        </Button>
      </div>

      {/* HEADER / NAV — Marca tipográfica premium */}
      <header className="absolute top-0 left-0 w-full z-40 px-6 py-4 flex justify-between items-center" data-id="element-7">
        <div className="leading-tight" data-id="element-8">
          <p className="text-white font-semibold uppercase tracking-[0.18em] md:tracking-[0.24em] text-base md:text-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]" data-id="element-9">
            Buceo y Navegación
          </p>
          <div className="mt-1.5 space-y-0.5 text-[9px] md:text-[11px] uppercase tracking-[0.22em] text-teal-300 leading-relaxed" data-id="element-10">
            <p data-id="element-11">Escuela de Buceo</p>
            <p data-id="element-12">Viajes Grupales</p>
            <p data-id="element-13">Agencia de Turismo HC 26.614</p>
          </div>
        </div>
        <div className="flex gap-4" data-id="element-14">
          <a href={whatsappLink} target="_blank" rel="noreferrer" className="text-white hover:text-teal-400 transition-colors bg-slate-900/40 p-2 rounded-full backdrop-blur-sm" data-id="element-15">
            <MessageCircle className="w-5 h-5" data-id="element-16" />
          </a>
          <a href={CONFIG.instagramUrl} target="_blank" rel="noreferrer" className="text-white hover:text-teal-400 transition-colors bg-slate-900/40 p-2 rounded-full backdrop-blur-sm" data-id="element-17">
            <Instagram className="w-5 h-5" data-id="element-18" />
          </a>
          <a href={`mailto:${CONFIG.email}`} className="text-white hover:text-teal-400 transition-colors bg-slate-900/40 p-2 rounded-full backdrop-blur-sm" data-id="element-19">
            <Mail className="w-5 h-5" data-id="element-20" />
          </a>
        </div>
      </header>

      {/* SECCIÓN 1 - HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-900" data-id="element-21">
        {/* Video Placeholder - Drop <video> tag here later (use poster={IMAGES.heroPoster}) */}
        <div className="absolute inset-0 z-0" data-id="element-22">
          <img src={IMAGES.heroPoster} alt="Bonaire" className="w-full h-full object-cover" data-id="element-23" />
          {/* Stronger dark overlays for maximum text legibility */}
          <div className="absolute inset-0 bg-slate-950/70" data-id="element-24" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/40 to-slate-950/90" data-id="element-25" />
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center text-white pt-44 md:pt-52 pb-16" data-id="element-26">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-4xl mx-auto flex flex-col items-center" data-id="element-27">
            {/* Premium exclusivity badges — inicio de la propuesta de valor */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-4" data-id="element-28">
              {['Viaje Grupal Exclusivo'].map(label => <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-teal-400/40 bg-teal-400/10 px-3 py-1.5 text-xs sm:text-sm font-semibold uppercase tracking-wider text-teal-200 backdrop-blur-md" data-id="element-29">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-300" data-id="element-30" />
                  {label}
                </span>)}
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-6xl md:text-8xl font-extrabold mb-5 tracking-tight uppercase drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]" data-id="element-31">
              Bonaire <span className="text-teal-400" data-id="element-32">2026</span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-xl md:text-3xl font-light mb-6 text-white/90 max-w-2xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]" data-id="element-33">
              Uno de los destinos de buceo más increíbles del mundo.
            </motion.p>

            <motion.p variants={fadeInUp} className="text-base md:text-lg font-semibold mb-3 text-teal-200 uppercase tracking-wide" data-id="element-34">
              Viaje grupal exclusivo organizado por Buceo y Navegación.
            </motion.p>

            <motion.div variants={fadeInUp} className="mb-10 space-y-2" data-id="element-35">
              <p className="text-sm md:text-base text-white/75 tracking-wide" data-id="element-36">
                Del 30 de agosto al 6 de septiembre
              </p>
              <p className="text-lg md:text-2xl font-semibold text-teal-300 tracking-wide" data-id="element-37">
                Grupo reducido de {CONFIG.totalSpots} buceadores
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-38">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-base md:text-lg font-bold uppercase tracking-wide px-8 h-14 rounded-full shadow-xl shadow-teal-500/20" onClick={scrollToParticipante} data-id="element-39">
                🤿 Quiero sumarme al grupo
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 text-base md:text-lg font-bold uppercase tracking-wide px-8 h-14 rounded-full bg-white/5 backdrop-blur-sm" onClick={() => window.open(whatsappLink, '_blank')} data-id="element-40">
                <MessageCircle className="w-5 h-5 mr-2" data-id="element-41" />
                Hablar por WhatsApp
              </Button>
            </motion.div>

            {/* Urgency / spots-left block (edit CONFIG.spotsLeft) */}
            <motion.div variants={fadeInUp} className="mt-8 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-5 py-2.5 backdrop-blur-md" data-id="element-42">
              <span className="text-lg" role="img" aria-label="urgencia" data-id="element-43">
                🔥
              </span>
              <span className="text-sm md:text-base font-bold text-amber-200" data-id="element-44">
                Quedan {CONFIG.spotsLeft} lugares disponibles
              </span>
            </motion.div>
            <motion.p variants={fadeInUp} className="mt-3 text-xs md:text-sm text-white/70" data-id="element-45">
              Una vez completado el grupo no se aceptarán nuevas reservas.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 2 - ¿POR QUÉ BONAIRE? */}
      <section className="py-24 bg-white" data-id="element-46">
        <div className="container mx-auto px-6" data-id="element-47">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-48">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-49">
              ¿Por qué tantos buzos sueñan con conocer Bonaire?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-50">
              Conocida como la capital mundial del buceo de costa, Bonaire
              ofrece una libertad sin igual.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-51">
            {[{
            icon: <Waves data-id="element-52" />,
            title: 'Shore diving ilimitado',
            desc: 'Libertad total para bucear a tu ritmo, 24/7.'
          }, {
            icon: <Fish data-id="element-53" />,
            title: 'Arrecifes espectaculares',
            desc: 'Vida marina abundante y corales prístinos a pocos metros de la costa.'
          }, {
            icon: <Sun data-id="element-54" />,
            title: 'Visibilidad excepcional',
            desc: 'Aguas cristalinas con visibilidad que a menudo supera los 30 metros.'
          }, {
            icon: <Anchor data-id="element-55" />,
            title: 'Klein Bonaire',
            desc: 'Inmersiones embarcadas en la isla deshabitada frente a la costa.'
          }, {
            icon: <CheckCircle2 data-id="element-56" />,
            title: 'Nitrox incluido',
            desc: 'Buceá más seguro y por más tiempo con aire enriquecido.'
          }, {
            icon: <Compass data-id="element-57" />,
            title: 'Agua cálida',
            desc: 'Temperaturas agradables todo el año, ideal para bucear con traje fino.'
          }].map((feature, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:shadow-lg transition-shadow" data-id="element-58">
                <div className="w-14 h-14 bg-teal-100 text-teal-600 rounded-2xl flex items-center justify-center mb-6" data-id="element-59">
                  {cloneElement(feature.icon as React.ReactElement, {
                className: 'w-7 h-7'
              })}
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900" data-id="element-60">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-61">{feature.desc}</p>
              </motion.div>)}
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 h-[400px]" data-id="element-62">
            {IMAGES.whyBonaire.map((img, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className={`rounded-3xl overflow-hidden ${i === 0 ? 'md:col-span-2' : ''}`} data-id="element-63">
                <img src={img} alt="Bonaire" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-id="element-64" />
              </motion.div>)}
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mt-12 flex justify-center" data-id="element-65">
            <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-base md:text-lg font-bold uppercase tracking-wide px-8 h-14 rounded-full shadow-xl shadow-teal-500/20" onClick={scrollToParticipante} data-id="element-66">
              🤿 Quiero sumarme al grupo
            </Button>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 3 - QUÉ INCLUYE */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-67">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-68">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-69">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-70">
              ¿Qué incluye el viaje?
            </h2>
            <p className="text-lg text-slate-300" data-id="element-71">
              Todo resuelto para que solo te preocupes por disfrutar.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-72">
            <Accordion type="single" collapsible className="w-full space-y-4" data-id="element-73">
              {[{
              title: '7 noches de alojamiento en Grand Windsock Bonaire',
              content: 'Alojamiento premium en villas compartidas, diseñadas específicamente para la comodidad de los buceadores, con espacios amplios y excelente ubicación.'
            }, {
              title: 'Desayuno americano incluido',
              content: 'Comenzá cada día con energía. Desayuno completo servido todas las mañanas antes de salir a bucear.'
            }, {
              title: 'Buceos ilimitados de costa',
              content: 'Acceso a tanques las 24 horas. Podés realizar tantas inmersiones de costa como desees, a tu propio ritmo.'
            }, {
              title: '2 inmersiones embarcadas en Klein Bonaire',
              content: 'Salida especial en barco para explorar los arrecifes vírgenes de la isla deshabitada de Klein Bonaire.'
            }, {
              title: 'Nitrox incluido',
              content: 'Para buceadores certificados en Aire Enriquecido, el Nitrox está incluido sin costo adicional para mayor seguridad y tiempos de fondo.',
              extra: <div className="mt-6 rounded-xl border border-teal-500/30 bg-teal-500/5 p-5" data-id="element-74">
                      <p className="font-semibold text-teal-300 mb-2" data-id="element-75">
                        ¿No tenés certificación de Nitrox?
                      </p>
                      <p className="text-slate-300 mb-4" data-id="element-76">
                        Podés realizar la certificación directamente durante el
                        viaje en Bonaire. Una excelente oportunidad opcional
                        para aprovechar al máximo las ventajas del aire
                        enriquecido y regresar del viaje con una certificación
                        adicional.
                      </p>
                      <ul className="space-y-2 mb-4" data-id="element-77">
                        {['Material de estudio', 'Parte teórica', 'Certificación internacional'].map(c => <li key={c} className="flex items-center gap-2 text-slate-300" data-id="element-78">
                            <CheckCircle2 className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-79" />
                            {c}
                          </li>)}
                      </ul>
                      <p className="text-sm text-slate-400" data-id="element-80">
                        Valor opcional:{' '}
                        <span className="font-semibold text-white" data-id="element-81">
                          USD 240
                        </span>
                      </p>
                    </div>
            }, {
              title: 'Vehículo para cada 4 participantes',
              content: 'Camionetas tipo pick-up ideales para cargar los equipos y moverse libremente por los puntos de buceo de la isla.'
            }, {
              title: 'Traslados aeropuerto ida y vuelta',
              content: 'Te esperamos en el aeropuerto de Bonaire y te llevamos al alojamiento, y viceversa el día de partida.'
            }, {
              title: 'BBQ grupal incluida',
              content: 'Una noche especial de asado para compartir anécdotas, relajar y fortalecer el grupo.'
            }, {
              title: 'Instructor acompañando durante todo el viaje',
              content: 'Asistencia, guía y organización constante por parte de nuestro equipo profesional para garantizar una experiencia perfecta.'
            }].map((item, i) => <AccordionItem key={i} value={`item-${i}`} className="bg-slate-800 border-slate-700 rounded-xl px-6 data-[state=open]:bg-slate-800/80 transition-colors" data-id="element-82">
                  <AccordionTrigger className="text-lg font-medium hover:no-underline py-6 text-left" data-id="element-83">
                    <div className="flex items-center gap-4" data-id="element-84">
                      <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0" data-id="element-85" />
                      {item.title}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 text-base pb-6 pl-10" data-id="element-86">
                    {item.content}
                    {(item as {
                  content: string;
                  extra?: React.ReactNode;
                }).extra}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 8 & 9 - INVERSIÓN Y RESERVA */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-87">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-88" />

        <div className="container mx-auto px-6 relative z-10" data-id="element-89">
          {/* Reserva primero - inversión inicial baja */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-2xl mx-auto bg-white rounded-3xl p-8 md:p-12 text-center text-slate-900 shadow-xl mb-16" data-id="element-90">
            <p className="text-sm font-semibold uppercase tracking-widest text-teal-600 mb-2" data-id="element-91">
              Reservá tu lugar
            </p>
            <div className="flex justify-center items-baseline text-5xl font-extrabold mb-6" data-id="element-92">
              <span className="text-2xl font-medium text-slate-400 mr-1" data-id="element-93">
                Seña USD
              </span>
              {CONFIG.depositAmount}
            </div>
            <p className="text-slate-600 mb-4 text-lg" data-id="element-94">
              Reservá tu lugar dentro del grupo realizando una seña equivalente
              a USD {CONFIG.depositAmount}.
            </p>
            <p className="text-slate-600 mb-4" data-id="element-95">
              Los lugares se asignan por orden de reserva y el grupo está
              limitado a {CONFIG.totalSpots} participantes.
            </p>
            <p className="text-sm text-slate-500 mb-8" data-id="element-96">
              Una vez realizada la reserva, nos pondremos en contacto para
              coordinar los siguientes pagos y responder cualquier consulta
              sobre el viaje.
            </p>
            <Button size="lg" className="w-full md:w-auto bg-teal-500 hover:bg-teal-600 text-white text-lg px-12 h-14 rounded-full" onClick={scrollToParticipante} data-id="element-97">
              🤿 Quiero sumarme al grupo
            </Button>
          </motion.div>

          {/* Inversión total del viaje */}
          <motion.div id="elegi-participante" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12 scroll-mt-24" data-id="element-98">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-99">
              Elegí cómo querés vivir Bonaire
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto" data-id="element-100">
              Viajá con la tranquilidad de tener organizados los aspectos más
              importantes de la experiencia, para que solo tengas que enfocarte
              en disfrutar Bonaire.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-101">
            <p className="text-center text-slate-400 mb-6" data-id="element-102">
              Elegí tu tipo de participante para continuar con la reserva.
            </p>

            {/* Selector de participante (radio group) */}
            <div role="radiogroup" aria-label="Tipo de participante" className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-103">
              {[{
              id: 'diver' as const,
              title: 'Buzo Certificado',
              subtitle: 'Programa completo de buceo',
              price: CONFIG.priceDiver
            }, {
              id: 'companion' as const,
              title: 'Acompañante No Buceador',
              subtitle: 'Disfrutá la experiencia en superficie',
              price: CONFIG.priceNonDiver
            }].map(opt => {
              const selected = packageType === opt.id;
              return <button key={opt.id} type="button" role="radio" aria-checked={selected} onClick={() => setPackageType(opt.id)} className={`text-left rounded-2xl p-5 border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 ${selected ? 'bg-slate-800 border-teal-500 ring-2 ring-teal-500/40' : 'bg-slate-800/40 border-slate-700 hover:border-slate-500'}`} data-id="element-104">
                    <div className="flex items-start gap-3" data-id="element-105">
                      <span className={`mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${selected ? 'border-teal-400' : 'border-slate-500'}`} data-id="element-106">
                        {selected && <span className="h-2.5 w-2.5 rounded-full bg-teal-400" data-id="element-107" />}
                      </span>
                      <div data-id="element-108">
                        <p className="text-lg font-bold text-white leading-tight" data-id="element-109">
                          {opt.title}
                        </p>
                        <p className="text-sm text-slate-400 mt-0.5" data-id="element-110">
                          {opt.subtitle}
                        </p>
                      </div>
                    </div>
                  </button>;
            })}
            </div>

            {/* Detalle dinámico según selección */}
            <div className="mt-6 rounded-3xl bg-white text-slate-900 p-8 md:p-10 shadow-xl" data-id="element-111">
              {packageType === 'diver' ? <div data-id="element-112">
                  <p className="text-sm font-semibold uppercase tracking-widest text-teal-600 mb-2" data-id="element-113">
                    Buzo Certificado
                  </p>
                  <div className="flex items-baseline mb-4" data-id="element-114">
                    <span className="text-xl font-medium text-slate-400 mr-2" data-id="element-115">
                      Total USD
                    </span>
                    <span className="text-5xl font-extrabold" data-id="element-116">
                      {CONFIG.priceDiver}
                    </span>
                  </div>
                  <p className="text-slate-600 mb-8 leading-relaxed" data-id="element-117">
                    Incluye alojamiento, desayunos, programa completo de buceo,
                    Nitrox, vehículo, traslados y actividades del grupo.
                  </p>
                  <Button size="lg" className="w-full bg-teal-500 hover:bg-teal-600 text-white text-base md:text-lg font-bold uppercase tracking-wide h-14 rounded-full" onClick={() => window.location.href = CONFIG.checkoutDiverUrl} data-id="element-118">
                    Reservar como buzo
                  </Button>
                </div> : <div data-id="element-119">
                  <p className="text-sm font-semibold uppercase tracking-widest text-teal-600 mb-2" data-id="element-120">
                    Acompañante No Buceador
                  </p>
                  <div className="flex items-baseline mb-4" data-id="element-121">
                    <span className="text-xl font-medium text-slate-400 mr-2" data-id="element-122">
                      Total USD
                    </span>
                    <span className="text-5xl font-extrabold" data-id="element-123">
                      {CONFIG.priceNonDiver}
                    </span>
                  </div>
                  <p className="text-slate-600 mb-8 leading-relaxed" data-id="element-124">
                    Incluye alojamiento, desayunos, vehículo, traslados y
                    actividades grupales.
                  </p>
                  <Button size="lg" className="w-full bg-teal-500 hover:bg-teal-600 text-white text-base md:text-lg font-bold uppercase tracking-wide h-14 rounded-full" onClick={() => window.location.href = CONFIG.checkoutCompanionUrl} data-id="element-125">
                    Reservar como acompañante
                  </Button>
                </div>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 4 & 5 - ALOJAMIENTO */}
      <section className="py-24 bg-slate-50" data-id="element-126">
        <div className="container mx-auto px-6" data-id="element-127">
          <div className="flex flex-col lg:flex-row gap-16 items-center mb-16" data-id="element-128">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="lg:w-1/2" data-id="element-129">
              <Badge className="bg-teal-100 text-teal-800 mb-4" data-id="element-130">
                Alojamiento Premium
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-131">
                Grand Windsock Bonaire
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-132">
                Ubicado a metros del mar y de los principales puntos de buceo de
                Bonaire. Un resort diseñado pensando en las necesidades del
                buceador, combinando lujo, comodidad y practicidad.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-133">
                {['Villas amplias y luminosas', 'Jardín tropical y piscina', 'Cocina totalmente equipada', 'Espacios comunes para relajar', 'Excelente ubicación para buceadores'].map((item, i) => <li key={i} className="flex items-center gap-3 text-slate-700 font-medium" data-id="element-134">
                    <div className="w-2 h-2 bg-teal-500 rounded-full" data-id="element-135" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="lg:w-1/2 grid grid-cols-2 gap-4" data-id="element-136">
              <div className="relative col-span-2 rounded-3xl overflow-hidden group" data-id="element-137">
                <img src={IMAGES.accommodation.exterior} alt="Exterior de la villa rodeada de jardín tropical" className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-138" />
                <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-139">
                  Exterior de la villa
                </span>
              </div>
              <div className="relative rounded-3xl overflow-hidden group" data-id="element-140">
                <img src={IMAGES.accommodation.terraza} alt="Terraza con lounge bajo palapa y jardín tropical" className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-141" />
                <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-142">
                  Terraza & jardín
                </span>
              </div>
              <div className="relative rounded-3xl overflow-hidden group" data-id="element-143">
                <img src={IMAGES.accommodation.habitacion} alt="Habitación en base doble con vista al jardín" className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-144" />
                <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-145">
                  Habitación doble
                </span>
              </div>
            </motion.div>
          </div>

          {/* Galería complementaria — espacios comunes y baño */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-16" data-id="element-146">
            <div className="relative sm:col-span-2 rounded-3xl overflow-hidden group" data-id="element-147">
              <img src={IMAGES.accommodation.living} alt="Espacios comunes amplios y luminosos con techo abovedado" className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-148" />
              <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-149">
                Espacios comunes
              </span>
            </div>
            <div className="relative rounded-3xl overflow-hidden group" data-id="element-150">
              <img src={IMAGES.accommodation.bano} alt="Baño privado moderno y luminoso" className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-151" />
              <span className="absolute bottom-3 left-4 text-white text-sm font-medium drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]" data-id="element-152">
                Baño privado
              </span>
            </div>
          </motion.div>

          {/* Distribución */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100" data-id="element-153">
            <h3 className="text-2xl font-bold mb-8 text-center text-slate-900" data-id="element-154">
              Distribución Exclusiva
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-155">
              <div className="text-center" data-id="element-156">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-157">
                  <MapPin className="w-8 h-8 text-teal-600" data-id="element-158" />
                </div>
                <h4 className="font-bold text-lg mb-2" data-id="element-159">3 Villas Exclusivas</h4>
                <p className="text-slate-600 text-sm" data-id="element-160">
                  Reservadas únicamente para nuestro grupo, garantizando un
                  ambiente privado y relajado.
                </p>
              </div>
              <div className="text-center" data-id="element-161">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-162">
                  <Users className="w-8 h-8 text-teal-600" data-id="element-163" />
                </div>
                <h4 className="font-bold text-lg mb-2" data-id="element-164">Base Doble</h4>
                <p className="text-slate-600 text-sm" data-id="element-165">
                  Habitaciones organizadas en base doble y configuradas con
                  camas individuales para cada ocupante. Un espacio cómodo y
                  pensado para brindar descanso, privacidad y comodidad después
                  de cada jornada de buceo.
                </p>
              </div>
              <div className="text-center" data-id="element-166">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-167">
                  <CheckCircle2 className="w-8 h-8 text-teal-600" data-id="element-168" />
                </div>
                <h4 className="font-bold text-lg mb-2" data-id="element-169">Baño Privado</h4>
                <p className="text-slate-600 text-sm" data-id="element-170">
                  Compartido únicamente por los ocupantes de la habitación, para
                  mayor comodidad.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN 6 - QUIÉNES SOMOS */}
      <section className="py-24 bg-white border-t border-slate-100" data-id="element-171">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-172">
          <div className="flex flex-col md:flex-row items-center gap-12 bg-slate-900 rounded-3xl p-8 md:p-12 text-white overflow-hidden relative" data-id="element-173">
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" data-id="element-174" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" data-id="element-175" />

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="md:w-1/3 flex justify-center relative z-10" data-id="element-176">
              <div className="text-center md:text-left" data-id="element-177">
                <p className="text-white font-bold uppercase tracking-[0.16em] text-3xl md:text-4xl leading-tight" data-id="element-178">
                  Buceo y<br data-id="element-179" />
                  Navegación
                </p>
                <div className="mx-auto md:mx-0 mt-4 h-px w-20 bg-teal-400/60" data-id="element-180" />
                <p className="mt-4 text-xs uppercase tracking-[0.2em] text-teal-300" data-id="element-181">
                  Escuela de Buceo · Viajes Grupales · Agencia de Turismo
                </p>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="md:w-2/3 relative z-10 text-center md:text-left" data-id="element-182">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-183">
                Organizado por Buceo y Navegación
              </h2>
              <p className="text-lg text-slate-300 mb-8" data-id="element-184">
                Más de 10 años formando buzos y organizando experiencias
                inolvidables alrededor del mundo.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left" data-id="element-185">
                {/* Tarjeta 1 - Instructor a cargo */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 h-full" data-id="element-186">
                  <p className="text-sm text-teal-400 font-semibold uppercase tracking-wider mb-2" data-id="element-187">
                    Instructor a cargo del viaje
                  </p>
                  <p className="text-xl font-bold mb-0.5" data-id="element-188">
                    Mauro Aníbal Mariscotti
                  </p>
                  <p className="text-slate-300 flex flex-wrap items-center gap-2 mb-3" data-id="element-189">
                    Instructor PADI
                    <span className="bg-slate-700 px-2 py-0.5 rounded text-xs" data-id="element-190">
                      #{CONFIG.instructorPadiNumber}
                    </span>
                  </p>
                  <p className="text-sm text-slate-400 leading-relaxed" data-id="element-191">
                    Responsable de la coordinación general del viaje,
                    actividades de buceo y acompañamiento del grupo durante toda
                    la experiencia en Bonaire.
                  </p>
                </div>

                {/* Tarjeta 2 - Asesor de viaje */}
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 h-full" data-id="element-192">
                  <p className="text-sm text-teal-400 font-semibold uppercase tracking-wider mb-2" data-id="element-193">
                    Asesor de viaje y atención personalizada
                  </p>
                  <p className="text-xl font-bold mb-0.5" data-id="element-194">
                    Fernando J. Alvarez
                  </p>
                  <p className="text-slate-300 flex flex-wrap items-center gap-2 mb-3" data-id="element-195">
                    Instructor PADI
                    <span className="bg-slate-700 px-2 py-0.5 rounded text-xs" data-id="element-196">
                      #342282
                    </span>
                  </p>
                  <p className="text-sm text-slate-400 leading-relaxed" data-id="element-197">
                    Disponible para responder consultas sobre el viaje,
                    reservas, certificaciones, requisitos de participación y
                    planificación previa al viaje.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 mt-8 justify-center md:justify-start" data-id="element-198">
                <Button variant="outline" className="border-slate-600 text-slate-900 bg-white hover:bg-slate-100 rounded-full" onClick={() => window.open(CONFIG.instagramUrl, '_blank')} data-id="element-199">
                  <Instagram className="w-4 h-4 mr-2" data-id="element-200" /> Seguinos en Instagram
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECCIÓN 7 - TESTIMONIOS */}
      <section className="py-24 bg-slate-50" data-id="element-201">
        <div className="container mx-auto px-6" data-id="element-202">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-203">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-204">
              Experiencias reales de nuestros viajeros
            </h2>
            <p className="text-lg text-slate-600" data-id="element-205">
              Experiencias reales de quienes ya viajaron con nosotros.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-206">
            {[{
            name: 'Martín G.',
            text: 'La organización fue impecable. Mauro está en todos los detalles. Volvería a viajar con ellos sin dudarlo.'
          }, {
            name: 'Laura S.',
            text: 'Bonaire es un sueño y hacerlo con este grupo lo hizo aún mejor. Excelente onda, profesionalismo y seguridad en el agua.'
          }, {
            name: 'Diego F.',
            text: 'Mi primer viaje de buceo internacional. Me sentí súper cuidado. Las villas increíbles y los buceos inolvidables.'
          }].map((testimonial, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-207">
                <Card className="h-full border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-208">
                  <CardHeader data-id="element-209">
                    <div className="flex text-yellow-400 mb-2" data-id="element-210">
                      {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-211" />)}
                    </div>
                    <CardTitle className="text-lg" data-id="element-212">
                      {testimonial.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent data-id="element-213">
                    <p className="text-slate-600 italic" data-id="element-214">
                      "{testimonial.text}"
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>

          <div className="text-center" data-id="element-215">
            <Button variant="outline" className="rounded-full border-slate-300 text-slate-700" onClick={() => window.open(CONFIG.googleReviewsUrl, '_blank')} data-id="element-216">
              Ver más reseñas en Google
            </Button>
          </div>
        </div>
      </section>

      {/* SECCIÓN 10 - PREGUNTAS FRECUENTES */}
      <section className="py-24 bg-slate-50" data-id="element-217">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-218">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-219">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-220">
              Preguntas Frecuentes
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-221">
            <Accordion type="single" collapsible className="w-full bg-white rounded-2xl p-2 shadow-sm border border-slate-100" data-id="element-222">
              {([{
              q: '¿Qué certificación necesito para participar?',
              a: 'Se requiere como mínimo la certificación Open Water Diver o cualquier certificación equivalente reconocida internacionalmente por las principales organizaciones de buceo.'
            }, {
              q: '¿Puedo realizar el curso Open Water Diver en Bonaire?',
              a: <div className="space-y-3" data-id="element-223">
                        <p data-id="element-224">
                          Sí. Existe la posibilidad de realizar el curso Open
                          Water Diver directamente en Bonaire.
                        </p>
                        <p data-id="element-225">
                          La disponibilidad debe consultarse previamente antes
                          de realizar la reserva.
                        </p>
                        <p data-id="element-226">
                          El curso tiene un costo adicional de{' '}
                          <span className="font-semibold text-slate-900" data-id="element-227">
                            USD 550
                          </span>{' '}
                          e incluye:
                        </p>
                        <ul className="space-y-2" data-id="element-228">
                          {['Material de estudio', 'Entrenamiento práctico', 'Certificación internacional'].map(c => <li key={c} className="flex items-center gap-2" data-id="element-229">
                              <CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0" data-id="element-230" />
                              {c}
                            </li>)}
                        </ul>
                      </div>
            }, {
              q: '¿Cómo funcionan los pagos?',
              a: `Reserva equivalente a USD ${CONFIG.depositAmount}. Hasta 30 días antes del viaje: USD 900. Saldo restante: se abona en destino.`
            }, {
              q: '¿El viaje incluye los pasajes aéreos?',
              a: <div className="space-y-3" data-id="element-231">
                        <p data-id="element-232">No.</p>
                        <p data-id="element-233">
                          El valor del viaje no incluye los pasajes aéreos hacia
                          Bonaire. De esta manera, cada participante puede
                          elegir la aerolínea, ruta y fechas que mejor se
                          adapten a sus necesidades.
                        </p>
                        <p data-id="element-234">
                          Una vez realizada la reserva, estaremos disponibles
                          para asesorarte y ayudarte a coordinar tu viaje para
                          que puedas llegar sin inconvenientes al destino.
                        </p>
                      </div>
            }, {
              q: '¿Necesito llevar mi propio equipo?',
              a: <div className="space-y-3" data-id="element-235">
                        <p data-id="element-236">
                          El equipamiento de buceo no está incluido dentro del
                          paquete.
                        </p>
                        <p data-id="element-237">
                          Podés llevar tu propio equipo o alquilarlo
                          directamente en Bonaire. Para quienes necesiten el
                          equipo completo durante toda la semana, el valor
                          aproximado es de{' '}
                          <span className="font-semibold text-slate-900" data-id="element-238">
                            USD 300
                          </span>
                          .
                        </p>
                        <p data-id="element-239">
                          Si ya contás con alguna parte del equipamiento, el
                          costo de alquiler disminuye según los elementos que
                          necesites.
                        </p>
                        <p data-id="element-240">
                          El alquiler se abona directamente en destino en
                          dólares billete.
                        </p>
                      </div>
            }, {
              q: '¿Puedo viajar con alguien que no bucea?',
              a: '¡Sí! Existe una tarifa especial para acompañantes no buceadores, y Bonaire ofrece hermosas playas y actividades en la superficie.'
            }, {
              q: '¿Puedo viajar solo?',
              a: <div className="space-y-3" data-id="element-241">
                        <p data-id="element-242">Sí.</p>
                        <p data-id="element-243">
                          Muchos participantes viajan solos y se integran al
                          grupo durante toda la experiencia.
                        </p>
                        <p data-id="element-244">
                          Las habitaciones se organizan en base doble y nos
                          encargamos de coordinar la distribución para
                          garantizar comodidad y una excelente convivencia
                          durante el viaje.
                        </p>
                      </div>
            }, {
              q: '¿Puedo solicitar una habitación privada?',
              a: <div className="space-y-3" data-id="element-245">
                        <p data-id="element-246">Sí.</p>
                        <p data-id="element-247">
                          Podés solicitar una habitación privada con baño
                          privado abonando un adicional de{' '}
                          <span className="font-semibold text-slate-900" data-id="element-248">
                            USD 500
                          </span>{' '}
                          sobre el valor del paquete.
                        </p>
                        <p data-id="element-249">
                          La disponibilidad es limitada y se recomienda
                          consultarla antes de realizar la reserva.
                        </p>
                      </div>
            }, {
              q: '¿Cuántas personas viajan?',
              a: `Es un grupo reducido y exclusivo de máximo ${CONFIG.totalSpots} participantes, para garantizar atención personalizada.`
            }, {
              q: '¿Quién acompaña el viaje?',
              a: 'Mauro Aníbal Mariscotti, Instructor PADI Certificado, estará acompañando y coordinando al grupo durante toda la estadía.'
            }] as Array<{
              q: string;
              a: React.ReactNode;
            }>).map((faq, i) => <AccordionItem key={i} value={`faq-${i}`} className="border-b border-slate-100 last:border-0 px-4" data-id="element-250">
                  <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-teal-600 py-5" data-id="element-251">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5" data-id="element-252">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* SECCIÓN FINAL */}
      <section className="relative py-32 bg-slate-900 flex items-center justify-center overflow-hidden" data-id="element-253">
        <div className="absolute inset-0 z-0" data-id="element-254">
          <img src={IMAGES.finalCta} alt="Bonaire Landscape" className="w-full h-full object-cover opacity-40" data-id="element-255" />
          <div className="absolute inset-0 bg-slate-900/60" data-id="element-256" />
        </div>

        <div className="relative z-10 container mx-auto px-6 text-center text-white" data-id="element-257">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-258">
            <h2 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-259">
              ¿Te gustaría vivir esta experiencia?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-260">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 h-14 rounded-full" onClick={scrollToParticipante} data-id="element-261">
                🤿 Quiero sumarme al grupo
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8 h-14 rounded-full bg-transparent" onClick={() => window.open(whatsappLink, '_blank')} data-id="element-262">
                Hablar por WhatsApp
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER SIMPLE — Marca tipográfica */}
      <footer className="bg-slate-950 py-12 text-center text-slate-500 text-sm" data-id="element-263">
        <div className="container mx-auto px-6" data-id="element-264">
          <p className="text-white font-semibold uppercase tracking-[0.22em] text-xl md:text-2xl mb-2" data-id="element-265">
            Buceo y Navegación
          </p>
          <p className="text-[11px] uppercase tracking-[0.2em] text-teal-300 mb-6" data-id="element-266">
            Escuela de Buceo · Viajes Grupales · Agencia de Turismo
          </p>
          <p data-id="element-267">© 2026 Buceo y Navegación. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>;
}